import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Button,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Grid,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { Card as CardType, FilterState } from '../types';
import Card from '../components/Card';
import { mockCards } from '../data/mockData';
import { recentHotCards } from '../data/recentHotCards';

// List of popular card brands
const cardBrands = [
  'All Brands',
  'Panini',
  'Topps',
  'Leaf',
  'Upper Deck',
  'Fleer',
  'Bowman',
  'Donruss',
  'Prizm',
  'Select',
  'Mosaic',
  'Optic',
  'National Treasures',
  'Immaculate',
  'Chronicles',
  'Revolution',
];

// Grade options by company
const gradeOptions = [
  { company: 'PSA', grades: ['10', '9', '8', '7', '6', '5'] },
  { company: 'BGS', grades: ['10', '9.5', '9', '8.5', '8'] },
  { company: 'SGC', grades: ['10', '9.5', '9', '8.5', '8'] }
];

const SearchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [cards, setCards] = useState<CardType[]>([]);
  const [filteredCards, setFilteredCards] = useState<CardType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    search: searchParams.get('q') || '',
    sport: 'all',
    player: '',
    team: '',
    year: [1980, 2025],
    brand: 'All Brands',
    condition: '',
    grade: '',
    priceRange: [0, 50000],
    isGraded: 'all',
  });
  const [expandedAccordions, setExpandedAccordions] = useState<{[key: string]: boolean}>({
    searchSport: true,
    playerTeam: false,
    yearBrand: false,
    conditionGrade: false,
    priceRange: false,
  });
  // Selected grade filters
  const [selectedGrades, setSelectedGrades] = useState<{ [key: string]: boolean }>({});
  
  // Reference to track scroll position
  const lastScrollTop = useRef<number>(0);

  // Effect to handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      const st = window.scrollY || document.documentElement.scrollTop;
      if (st < lastScrollTop.current) {
        // Scrolling up - close all accordions
        setExpandedAccordions({
          searchSport: false,
          playerTeam: false,
          yearBrand: false,
          conditionGrade: false,
          priceRange: false,
        });
      }
      lastScrollTop.current = st <= 0 ? 0 : st;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle accordion state changes
  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordions(prev => ({
      ...prev,
      [panel]: isExpanded
    }));
  };

  // Handle grade checkbox changes
  const handleGradeChange = (grade: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSelectedGrades = {
      ...selectedGrades,
      [grade]: event.target.checked
    };
    
    setSelectedGrades(newSelectedGrades);
    
    // Update the grade filter string based on selected grades
    const selectedGradesList = Object.keys(newSelectedGrades).filter(key => newSelectedGrades[key]);
    const gradeFilterString = selectedGradesList.join(',');
    
    setFilters(prev => ({
      ...prev,
      grade: gradeFilterString
    }));
  };

  useEffect(() => {
    const loadCards = async () => {
      try {
        setLoading(true);
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Combine both data sources
        const allCards = [...recentHotCards, ...mockCards];
        
        // Remove any duplicates (if any cards appear in both datasets)
        const uniqueCards = allCards.filter((card, index, self) =>
          index === self.findIndex((c) => c.id === card.id)
        );
        
        setCards(uniqueCards);
        setError(null);
      } catch (err) {
        setError('Failed to load cards. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadCards();
  }, []);

  useEffect(() => {
    // Apply filters
    let filtered = cards;

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(card =>
        card.player.toLowerCase().includes(searchLower) ||
        card.team.toLowerCase().includes(searchLower) ||
        card.title.toLowerCase().includes(searchLower)
      );
    }

    if (filters.sport !== 'all') {
      filtered = filtered.filter(card => card.sport === filters.sport);
    }

    if (filters.player) {
      filtered = filtered.filter(card =>
        card.player.toLowerCase().includes(filters.player.toLowerCase())
      );
    }

    if (filters.team) {
      filtered = filtered.filter(card =>
        card.team.toLowerCase().includes(filters.team.toLowerCase())
      );
    }

    if (filters.year[0] !== 1980 || filters.year[1] !== 2025) {
      filtered = filtered.filter(card =>
        card.year >= filters.year[0] && card.year <= filters.year[1]
      );
    }

    if (filters.brand && filters.brand !== 'All Brands') {
      filtered = filtered.filter(card =>
        card.brand.toLowerCase().includes(filters.brand.toLowerCase())
      );
    }

    if (filters.condition) {
      filtered = filtered.filter(card =>
        card.condition.toLowerCase().includes(filters.condition.toLowerCase())
      );
    }

    // Filter by specific grades if any are selected
    if (filters.grade) {
      const selectedGrades = filters.grade.split(',');
      if (selectedGrades.length > 0) {
        filtered = filtered.filter(card => {
          if (!card.grade) return false;
          
          // Check if any of the selected grades match this card
          return selectedGrades.some(selectedGrade => 
            card.grade?.toLowerCase().includes(selectedGrade.toLowerCase())
          );
        });
      }
    }

    if (filters.priceRange[0] !== 0 || filters.priceRange[1] !== 50000) {
      filtered = filtered.filter(card =>
        card.price >= filters.priceRange[0] && card.price <= filters.priceRange[1]
      );
    }

    if (filters.isGraded !== 'all') {
      filtered = filtered.filter(card =>
        filters.isGraded === 'graded' ? card.isGraded : !card.isGraded
      );
    }

    setFilteredCards(filtered);
  }, [cards, filters]);

  // Reset grade selection when switching away from graded only
  useEffect(() => {
    if (filters.isGraded !== 'graded') {
      setSelectedGrades({});
      setFilters(prev => ({ ...prev, grade: '' }));
    }
  }, [filters.isGraded]);

  const handleCardClick = (card: CardType) => {
    window.open(card.ebayUrl, '_blank');
  };

  const handleFilterChange = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      sport: 'all',
      player: '',
      team: '',
      year: [1980, 2025],
      brand: 'All Brands',
      condition: '',
      grade: '',
      priceRange: [0, 50000],
      isGraded: 'all',
    });
    setSelectedGrades({});
  };

  const formatPrice = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h3" sx={{ fontWeight: 600, mb: 4 }}>
        Search Cards
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Filters Sidebar */}
        <Box sx={{ width: { xs: '100%', md: '25%' } }}>
          <Paper sx={{ 
            p: 3, 
            position: 'sticky', 
            top: 20,
            maxHeight: 'calc(100vh - 40px)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Filters</Typography>
              <Button size="small" onClick={clearFilters}>
                Clear All
              </Button>
            </Box>

            {/* Scrollable filter content */}
            <Box sx={{ 
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '10px',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                }
              },
              pr: 1 // Add padding right to prevent content being covered by scrollbar
            }}>
              <Accordion 
                expanded={expandedAccordions.searchSport}
                onChange={handleAccordionChange('searchSport')}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Search & Sport</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    fullWidth
                    label="Search"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <FormControl fullWidth>
                    <InputLabel>Sport</InputLabel>
                    <Select
                      value={filters.sport}
                      label="Sport"
                      onChange={(e) => handleFilterChange('sport', e.target.value)}
                    >
                      <MenuItem value="all">All Sports</MenuItem>
                      <MenuItem value="basketball">Basketball</MenuItem>
                      <MenuItem value="football">Football</MenuItem>
                      <MenuItem value="baseball">Baseball</MenuItem>
                    </Select>
                  </FormControl>
                </AccordionDetails>
              </Accordion>

              <Accordion 
                expanded={expandedAccordions.playerTeam}
                onChange={handleAccordionChange('playerTeam')}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Player & Team</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    fullWidth
                    label="Player"
                    value={filters.player}
                    onChange={(e) => handleFilterChange('player', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Team"
                    value={filters.team}
                    onChange={(e) => handleFilterChange('team', e.target.value)}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion 
                expanded={expandedAccordions.yearBrand}
                onChange={handleAccordionChange('yearBrand')}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Year & Brand</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography gutterBottom>Year Range</Typography>
                  <Slider
                    value={filters.year}
                    onChange={(_, value) => handleFilterChange('year', value)}
                    valueLabelDisplay="auto"
                    min={1980}
                    max={2025}
                    sx={{ mb: 3 }}
                  />
                  <FormControl fullWidth>
                    <InputLabel>Brand</InputLabel>
                    <Select
                      value={filters.brand}
                      label="Brand"
                      onChange={(e) => handleFilterChange('brand', e.target.value)}
                    >
                      {cardBrands.map((brand) => (
                        <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </AccordionDetails>
              </Accordion>

              <Accordion 
                expanded={expandedAccordions.conditionGrade}
                onChange={handleAccordionChange('conditionGrade')}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Condition & Grade</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    fullWidth
                    label="Condition"
                    value={filters.condition}
                    onChange={(e) => handleFilterChange('condition', e.target.value)}
                    sx={{ mb: 2 }}
                    disabled={filters.isGraded === 'graded'}
                  />
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Graded Status</InputLabel>
                    <Select
                      value={filters.isGraded}
                      label="Graded Status"
                      onChange={(e) => handleFilterChange('isGraded', e.target.value)}
                    >
                      <MenuItem value="all">All Cards</MenuItem>
                      <MenuItem value="graded">Graded Only</MenuItem>
                      <MenuItem value="ungraded">Ungraded Only</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Additional grade options appear when "Graded Only" is selected */}
                  {filters.isGraded === 'graded' && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Select Grade:
                      </Typography>
                      {gradeOptions.map((company) => (
                        <Box key={company.company} sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="medium" gutterBottom>
                            {company.company}
                          </Typography>
                          <Grid container spacing={1}>
                            {company.grades.map((grade) => {
                              const gradeKey = `${company.company} ${grade}`;
                              return (
                                <Grid item xs={6} key={gradeKey}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={!!selectedGrades[gradeKey]}
                                        onChange={handleGradeChange(gradeKey)}
                                        size="small"
                                      />
                                    }
                                    label={`${company.company} ${grade}`}
                                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Box>
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              <Accordion 
                expanded={expandedAccordions.priceRange}
                onChange={handleAccordionChange('priceRange')}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Price Range</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography gutterBottom>
                    {formatPrice(filters.priceRange[0])} - {formatPrice(filters.priceRange[1])}
                  </Typography>
                  <Slider
                    value={filters.priceRange}
                    onChange={(_, value) => handleFilterChange('priceRange', value)}
                    valueLabelDisplay="auto"
                    min={0}
                    max={50000}
                    valueLabelFormat={formatPrice}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          </Paper>
        </Box>

        {/* Results */}
        <Box sx={{ width: { xs: '100%', md: '75%' } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={60} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error}
            </Alert>
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6">
                  {filteredCards.length} cards found
                </Typography>
              </Box>

              {filteredCards.length > 0 ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
                  {filteredCards.map((card) => (
                    <Card key={card.id} card={card} onCardClick={handleCardClick} />
                  ))}
                </Box>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    No cards match your search criteria
                  </Typography>
                  <Button variant="contained" onClick={clearFilters} sx={{ mt: 2 }}>
                    Clear Filters
                  </Button>
                </Paper>
              )}
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default SearchPage; 